import { get_token } from "@/router";

const hls_js_url = process.env.VUE_APP_HLS_JS_URL || "https://cdn.jsdelivr.net/npm/hls.js@1";

export default {
  data() {
    return ({
      hls: null,
      attached_stream_url: "",
      hls_error_count: 0
    });
  },
  methods: {
    build_hls_config() {
      return ({
        lowLatencyMode: true,
        liveSyncDurationCount: 1,
        liveMaxLatencyDurationCount: 3,
        maxLiveSyncPlaybackRate: 1.5,
        backBufferLength: 0,
        maxBufferLength: 4,
        maxMaxBufferLength: 8,
        manifestLoadingMaxRetry: 8,
        levelLoadingMaxRetry: 8,
        fragLoadingMaxRetry: 8,
        xhrSetup: (xhr) => {
          const token = get_token();
          if (token) {
            xhr.setRequestHeader("Authorization", `Token ${token}`);
          }
        }
      });
    },
    async attach_hls_player() {
      const video = this.$refs.video;
      if (!video) {
        return;
      }

      const Hls = await this.load_hls_js();
      if (!Hls?.isSupported()) {
        throw new Error("This browser cannot play HLS live streams.");
      }

      this.hls = new Hls(this.build_hls_config());
      this.hls.loadSource(this.live_feed_src);
      this.hls.attachMedia(video);
      this.attached_stream_url = this.stream_url;
      this.register_hls_events(Hls, video);
    },
    register_hls_events(Hls, video) {
      this.hls.on(Hls.Events.MANIFEST_PARSED, async () => {
        this.seek_to_live_edge();
        await video.play().catch(() => undefined);
        this.mark_feed_loaded();
      });
      this.hls.on(Hls.Events.LEVEL_UPDATED, () => {
        this.seek_to_live_edge();
      });
      this.hls.on(Hls.Events.ERROR, (_event, data) => {
        this.handle_hls_error(Hls, data);
      });
    },
    handle_hls_error(Hls, data) {
      if (!data?.fatal) {
        return;
      }

      this.hls_error_count += 1;
      this.status_message = "Reconnecting live stream...";
      this.is_loading = true;

      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        this.hls.startLoad();
        return;
      }

      if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
        this.hls.recoverMediaError();
        return;
      }

      if (this.hls_error_count <= 5) {
        this.reconnect_hls_after_delay();
        return;
      }

      this.status_message = "The live stream disconnected.";
      this.image_failed = true;
      this.is_loading = false;
    },
    load_hls_js() {
      if (window.Hls) {
        return Promise.resolve(window.Hls);
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = hls_js_url;
        script.async = true;
        script.onload = () => resolve(window.Hls);
        script.onerror = () => reject(new Error("Could not load the HLS player."));
        document.head.appendChild(script);
      });
    },
    destroy_hls_player() {
      if (this.hls) {
        this.hls.destroy();
        this.hls = null;
      }

      this.attached_stream_url = "";

      const video = this.$refs.video;
      if (video) {
        video.removeAttribute("src");
        video.load();
      }
    },
    seek_to_live_edge() {
      const video = this.$refs.video;
      const live_position = this.hls?.liveSyncPosition;
      if (!video || !Number.isFinite(live_position)) {
        return;
      }

      if (live_position - video.currentTime > 3) {
        video.currentTime = live_position;
      }
    },
    attach_native_video(src) {
      const video = this.$refs.video;
      if (!video) {
        return;
      }

      video.src = src;
      video.load();
      video.play().catch(() => undefined);
    }
  }
};
